import { useEffect, useId, useRef, useState } from 'react';
import {
  ArrowLeftRight,
  Bell,
  Dumbbell,
  ImagePlus,
  MessageSquare,
  MessagesSquare,
  UserPlus,
} from 'lucide-react';
import { useApp } from '../state/AppContext';
import type { Notification } from '../state/types';

const PREVIEW_LIMIT = 8;

function relativeTime(date: string) {
  const difference = Math.max(0, Date.now() - new Date(date).getTime());
  if (difference < 60_000) return 'Just now';
  if (difference < 3_600_000) return `${Math.floor(difference / 60_000)}m ago`;
  if (difference < 86_400_000) return `${Math.floor(difference / 3_600_000)}h ago`;
  return `${Math.floor(difference / 86_400_000)}d ago`;
}

function NoticeIcon({ type }: { type: string }) {
  const size = 14;
  switch (type) {
    case 'reactions':
      return <Dumbbell size={size} aria-hidden="true" />;
    case 'messages':
    case 'comments':
      return <MessageSquare size={size} aria-hidden="true" />;
    case 'follows':
      return <UserPlus size={size} aria-hidden="true" />;
    case 'moderation':
      return <ImagePlus size={size} aria-hidden="true" />;
    case 'forum':
      return <MessagesSquare size={size} aria-hidden="true" />;
    case 'trade':
      return <ArrowLeftRight size={size} aria-hidden="true" />;
    default:
      return <Bell size={size} aria-hidden="true" />;
  }
}

export function NotificationMenu({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { state, save, navigate } = useApp();
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [busyId, setBusyId] = useState<string | null>(null);
  const unread = state.notifications.filter((item) => !item.read).length;
  const latest = [...state.notifications]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, PREVIEW_LIMIT);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  async function openNotice(item: Notification) {
    if (busyId) return;
    setBusyId(item.id);
    try {
      if (!item.read) {
        await save({
          notifications: state.notifications.map((notice) =>
            notice.id === item.id ? { ...notice, read: true } : notice,
          ),
        });
      }
      onOpenChange(false);
      navigate(item.path);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="notification-menu" ref={rootRef}>
      <button
        type="button"
        className="icon-button notification-button"
        aria-label={
          unread > 0 ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => onOpenChange(!open)}
      >
        <Bell size={20} />
        {unread > 0 && <i />}
      </button>
      {open && (
        <div
          className="notification-menu-panel elevation-1"
          id={listId}
          role="dialog"
          aria-label="Latest notifications"
        >
          <div className="notification-menu-heading">
            <strong>Notifications</strong>
            {unread > 0 ? <span>{unread} unread</span> : <span>You’re all caught up</span>}
          </div>
          {latest.length ? (
            <ul className="notification-menu-list">
              {latest.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`notification-menu-item${!item.read ? ' is-unread' : ''}`}
                    disabled={busyId === item.id}
                    onClick={() => void openNotice(item)}
                  >
                    <span className={`notification-menu-icon c-notice-${item.type}`}>
                      <NoticeIcon type={item.type} />
                    </span>
                    <span className="notification-menu-copy">
                      <strong>{item.title}</strong>
                      <span>{item.body}</span>
                      <small>{relativeTime(item.created_at)}</small>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="notification-menu-empty">No notifications yet.</p>
          )}
          <button
            type="button"
            className="notification-menu-all"
            onClick={() => {
              onOpenChange(false);
              navigate('/notifications');
            }}
          >
            See all notifications
          </button>
        </div>
      )}
    </div>
  );
}
